import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Search } from 'lucide-react';
import {
  api,
  type DatalakeClientHealth,
  type DatalakeClientOutcome,
  type DatalakeClientRunPoint,
  type DatalakeClientSummary,
} from '../lib/api';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Drawer } from '../components/ui/Drawer';
import { Fact, FactSection } from '../components/ui/Facts';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import { formatBytes, formatDateTime, formatNumber, formatRelative } from '../lib/format';
import { cn } from '../lib/utils';

const STATUS_LABEL: Record<DatalakeClientHealth, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  failing: 'Failing',
  stale: 'Stale',
  absent: 'Not discovered',
  unknown: 'Unknown',
};

const OUTCOME_LABEL: Record<DatalakeClientOutcome, string> = {
  ok: 'Pulled',
  partial: 'Some dates failed',
  failed: 'All dates failed',
  'discovery-failed': 'Unreachable',
  'up-to-date': 'Nothing to pull',
  absent: 'Not discovered',
  'not-in-scope': 'Not in this run',
};

type StatusFilter = 'all' | 'problems' | DatalakeClientHealth;

const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'problems', label: 'Problems' },
  { value: 'all', label: 'All' },
  { value: 'failing', label: 'Failing' },
  { value: 'stale', label: 'Stale' },
  { value: 'absent', label: 'Not discovered' },
];

const WINDOWS = [7, 14, 30, 60, 90];

const getValue = (item: DatalakeClientSummary, key: string): unknown => {
  switch (key) {
    case 'clientName':
      return item.clientName;
    case 'status':
      return item.status;
    case 'consecutiveFailures':
      return item.consecutiveFailures;
    case 'lastSuccess':
      return item.lastSuccess?.at ?? '';
    case 'latestDataDate':
      return item.latestDataDate ?? '';
    case 'dataLagDays':
      return item.dataLagDays ?? -1;
    default:
      return undefined;
  }
};

export function DatalakeCustomers() {
  const [days, setDays] = useState(30);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('problems');
  const [detail, setDetail] = useState<DatalakeClientSummary | null>(null);

  const health = useQuery({
    queryKey: ['datalake', 'customers', days],
    queryFn: () => api.getDatalakeCustomers(days),
    staleTime: 5 * 60 * 1000,
  });

  const all = useMemo(() => health.data?.clients ?? [], [health.data]);
  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((c) => {
      if (filter === 'problems' && !c.problem) return false;
      if (filter !== 'all' && filter !== 'problems' && c.status !== filter) return false;
      if (!needle) return true;
      return (
        c.clientName.toLowerCase().includes(needle) ||
        (c.serverIp ?? '').includes(needle) ||
        (c.lastError?.reason ?? '').toLowerCase().includes(needle)
      );
    });
  }, [all, filter, q]);

  const { sortedItems, sort, toggle } = useSort(items, getValue, {
    key: 'clientName',
    direction: 'asc',
  });

  const summary = health.data?.summary;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Datalake · Customers</h1>
        <p className="text-muted-foreground">
          Every X5 server the pipeline discovered, pivoted across the last {days} days of runs —
          who failed, why, and when they last pulled data.
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Tile label="Servers" value={formatNumber(summary.clients)} />
          <Tile label="Failing" value={formatNumber(summary.failing)} tone="warn" />
          <Tile label="Degraded" value={formatNumber(summary.degraded)} tone="caution" />
          <Tile label="Stale" value={formatNumber(summary.stale)} tone="caution" />
          <Tile label="Not discovered" value={formatNumber(summary.absent)} tone="warn" />
        </div>
      )}

      {health.data?.coverageAvailable === false && (
        <p className="flex items-start gap-2 text-sm text-caution">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          The Glue partition read failed, so staleness is unknown for this view. A server reporting
          "nothing to pull" cannot be checked against what actually landed in the lake.
        </p>
      )}

      {health.error && (
        <p className="text-sm text-destructive">{(health.error as Error).message}</p>
      )}

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          <div className="relative w-72">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer, server IP, reason…"
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-0.5 rounded-md border border-line p-0.5">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={cn(
                  'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                  filter === f.value
                    ? 'bg-accent-soft text-accent'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-32"
            aria-label="Run window"
          >
            {WINDOWS.map((d) => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </Select>
          <span className="ml-auto text-xs text-muted-foreground">
            {health.isLoading
              ? 'Loading…'
              : `${formatNumber(sortedItems.length)} of ${formatNumber(all.length)}`}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-muted/50">
              <tr>
                <SortableHeader label="Customer" sortKey="clientName" sort={sort} onToggle={toggle} />
                <SortableHeader label="Status" sortKey="status" sort={sort} onToggle={toggle} />
                <SortableHeader
                  label="Failing runs"
                  sortKey="consecutiveFailures"
                  sort={sort}
                  onToggle={toggle}
                />
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Last error</th>
                <SortableHeader
                  label="Last pulled"
                  sortKey="lastSuccess"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader
                  label="Newest data"
                  sortKey="latestDataDate"
                  sort={sort}
                  onToggle={toggle}
                />
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Recent runs</th>
              </tr>
            </thead>
            <tbody>
              {health.isLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {!health.isLoading &&
                sortedItems.map((c) => (
                  <tr
                    key={c.clientName}
                    onClick={() => setDetail(c)}
                    className={cn(
                      'cursor-pointer border-b border-line-soft last:border-0 hover:bg-raise',
                      c.problem && 'shadow-[inset_2px_0_0_var(--color-warn)]',
                    )}
                  >
                    <td className="px-4 py-2 font-medium">
                      {c.clientName}
                      {c.serverIp && (
                        <span className="ml-2 font-mono text-xs text-muted-foreground">
                          {c.serverIp}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-4 py-2 tabular-nums">
                      {c.consecutiveFailures > 0 ? (
                        <span className="font-medium text-destructive">
                          {c.consecutiveFailures}
                          {c.failingSince && (
                            <span className="ml-1.5 font-normal text-muted-foreground">
                              since {formatRelative(c.failingSince)}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="max-w-xs px-4 py-2">
                      {c.lastError ? (
                        <span className="text-xs">
                          <Badge variant="destructive">{c.lastError.reason ?? 'ERROR'}</Badge>
                          <span className="ml-2 text-muted-foreground">
                            {truncate(c.lastError.detail)}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {c.lastSuccess ? (
                        <>
                          <div>{formatRelative(c.lastSuccess.at)}</div>
                          <div className="text-muted-foreground">
                            {formatNumber(c.lastSuccess.datesSucceeded)} date
                            {c.lastSuccess.datesSucceeded === 1 ? '' : 's'} ·{' '}
                            {formatBytes(c.lastSuccess.rawBytes)}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">never in window</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <DataFreshness client={c} />
                    </td>
                    <td className="px-4 py-2">
                      <Sparkline history={c.history} />
                    </td>
                  </tr>
                ))}
              {!health.isLoading && sortedItems.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                    {filter === 'problems'
                      ? 'Every discovered server pulled cleanly in this window.'
                      : 'No servers match.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {summary && summary.partialScopeRuns > 0 && (
        <p className="text-xs text-muted-foreground">
          {summary.partialScopeRuns} of {summary.runs} runs in this window covered only some
          customers — a single-client backfill rather than the nightly sweep. Those runs count as
          evidence for the customers they name and are ignored for everyone else.
        </p>
      )}

      <Drawer
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={
          detail && (
            <div>
              <h2 className="text-base font-semibold">{detail.clientName}</h2>
              <p className="text-xs text-muted-foreground">
                {STATUS_LABEL[detail.status]}
                {detail.serverIp ? ` · ${detail.serverIp}` : ''}
              </p>
            </div>
          )
        }
      >
        {detail && <CustomerDetail client={detail} />}
      </Drawer>
    </div>
  );
}

function CustomerDetail({ client }: { client: DatalakeClientSummary }) {
  return (
    <div className="space-y-5">
      {client.lastError && (
        <FactSection title="Last error">
          <Fact label="Category" value={client.lastError.reason} />
          <Fact
            label="Scope"
            value={client.lastError.scope === 'discovery' ? 'Reading the archive index' : 'One date'}
          />
          <Fact label="When" value={formatDateTime(client.lastError.at)} />
          <Fact label="Run" value={client.lastError.runId} mono />
          <Fact label="Detail" value={client.lastError.detail} mono />
        </FactSection>
      )}

      <FactSection title="Last successful pull">
        {client.lastSuccess ? (
          <>
            <Fact label="When" value={formatDateTime(client.lastSuccess.at)} />
            <Fact label="Run" value={client.lastSuccess.runId} mono />
            <Fact label="Dates pulled" value={formatNumber(client.lastSuccess.datesSucceeded)} />
            <Fact
              label="Date range"
              value={
                client.lastSuccess.firstDate && client.lastSuccess.lastDate
                  ? `${client.lastSuccess.firstDate} → ${client.lastSuccess.lastDate}`
                  : null
              }
              mono
            />
            <Fact label="Raw" value={formatBytes(client.lastSuccess.rawBytes)} />
            <Fact label="Parquet" value={formatBytes(client.lastSuccess.parquetBytes)} />
            <Fact
              label="Empty dates"
              value={client.lastSuccess.datesEmpty > 0 ? formatNumber(client.lastSuccess.datesEmpty) : null}
            />
          </>
        ) : (
          <Fact label="When" value="No run in this window pulled data" />
        )}
      </FactSection>

      <FactSection title="In the lake">
        <Fact label="Newest partition" value={client.latestDataDate} mono />
        <Fact
          label="Behind by"
          value={client.dataLagDays !== null ? `${client.dataLagDays} days` : null}
        />
        <Fact
          label="Coverage"
          value={client.noDataInWindow ? 'No partition found in the lookback' : null}
        />
      </FactSection>

      <div>
        <h3 className="pb-1.5 text-sm font-semibold">Run history ({client.history.length})</h3>
        <div className="divide-y divide-line-soft border-t border-line-soft">
          {client.history.map((p) => (
            <div key={p.s3Key} className="flex items-start justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="text-xs">{formatDateTime(p.timestamp)}</div>
                <div className="text-xs text-muted-foreground">
                  {OUTCOME_LABEL[p.outcome]}
                  {p.datesSucceeded > 0 && ` · ${formatNumber(p.datesSucceeded)} dates`}
                  {p.datesFailed > 0 && ` · ${formatNumber(p.datesFailed)} failed`}
                  {p.rawBytes > 0 && ` · ${formatBytes(p.rawBytes)}`}
                </div>
                {p.detail && (
                  <div className="mt-0.5 font-mono text-[11px] break-all text-destructive">
                    {truncate(p.detail, 160)}
                  </div>
                )}
              </div>
              <OutcomeDot outcome={p.outcome} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DataFreshness({ client }: { client: DatalakeClientSummary }) {
  if (client.noDataInWindow) {
    return <span className="text-warn">none in window</span>;
  }
  if (!client.latestDataDate) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <>
      <div className="font-mono">{client.latestDataDate}</div>
      {client.dataLagDays !== null && (
        <div className={cn('text-muted-foreground', client.status === 'stale' && 'text-caution')}>
          {client.dataLagDays}d behind
        </div>
      )}
    </>
  );
}

/**
 * One cell per run, newest on the right so the row reads like a calendar. The
 * shape of a failure matters more than any single run: a solid block of red is
 * a server nobody noticed, a single mark is a blip.
 */
function Sparkline({ history }: { history: DatalakeClientRunPoint[] }) {
  const recent = history.slice(0, 21).reverse();
  return (
    <div className="flex items-end gap-0.5">
      {recent.map((p) => (
        <span
          key={p.s3Key}
          title={`${formatDateTime(p.timestamp)} — ${OUTCOME_LABEL[p.outcome]}`}
          className={cn('h-4 w-1.5 rounded-[1px]', outcomeClass(p.outcome))}
        />
      ))}
    </div>
  );
}

function OutcomeDot({ outcome }: { outcome: DatalakeClientOutcome }) {
  return <span className={cn('mt-1 h-2.5 w-2.5 shrink-0 rounded-full', outcomeClass(outcome))} />;
}

function outcomeClass(outcome: DatalakeClientOutcome): string {
  switch (outcome) {
    case 'ok':
      return 'bg-ok';
    case 'partial':
      return 'bg-caution';
    case 'failed':
    case 'discovery-failed':
      return 'bg-warn';
    case 'up-to-date':
      return 'bg-idle';
    case 'absent':
      return 'bg-destructive';
    default:
      return 'bg-line';
  }
}

function StatusBadge({ status }: { status: DatalakeClientHealth }) {
  const label = STATUS_LABEL[status];
  switch (status) {
    case 'failing':
    case 'absent':
      return <Badge variant="destructive">{label}</Badge>;
    case 'degraded':
    case 'stale':
      return <Badge variant="warning">{label}</Badge>;
    case 'healthy':
      return <Badge variant="success">{label}</Badge>;
    default:
      return <Badge variant="secondary">{label}</Badge>;
  }
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'warn' | 'caution';
}) {
  return (
    <Card className="px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={cn(
          'text-2xl font-bold tabular-nums',
          tone === 'warn' && value !== '0' && 'text-warn',
          tone === 'caution' && value !== '0' && 'text-caution',
        )}
      >
        {value}
      </div>
    </Card>
  );
}

function truncate(text: string | null, max = 80): string {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max)}…` : text;
}
